import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AnthropicBlock,
  ApiError,
  ChatHistoryRow,
  clearChat,
  ExecutionRow,
  getChatHistory,
  listExecutions,
  rawFileUrl,
  streamChat,
} from "../lib/api";
import { AskClaudeRequest, buildAskMessage } from "../lib/askMessage";
import ToolChip from "./ToolChip";

/**
 * Reviewer-facing chat panel.
 *
 * Wires the SSE stream from `streamChat()` into a live transcript:
 * - Past messages come from `GET /chat` and don't stream.
 * - The currently-streaming assistant message accumulates `delta` text and a
 *   running list of tool chips. As each `tool_use_end` arrives we patch the
 *   matching chip with its result.
 * - When `done` fires, we flush the streaming message into the history list.
 *
 * Tool calls are always shown inline (collapsible) so the reviewer can audit
 * what files Claude actually touched. Plan-style text is rendered as part
 * of the assistant message — Claude is instructed to write the plan as plain
 * prose, so no special rendering is needed.
 */

type LiveTool = {
  id: string;
  name: string;
  input?: Record<string, unknown>;
  resultPreview?: string;
  artifactPath?: string | null;
  /** Set by tool_use_end when the tool returned an execution_id (run_on_aws). */
  executionId?: number | null;
  isError?: boolean;
};

type LiveAssistant = {
  text: string;
  tools: LiveTool[];
};

export default function ChatBox(props: {
  sessionId: number | null;
  /** Select-to-ask (issue #56): a new nonce auto-sends the composed message. */
  askRequest?: AskClaudeRequest | null;
}) {
  const [history, setHistory] = useState<ChatHistoryRow[]>([]);
  const [executions, setExecutions] = useState<ExecutionRow[]>([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState<LiveAssistant | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<
    | { kind: "auth"; message: string }
    | { kind: "generic"; message: string }
    | null
  >(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Pull executions + history together so historical run_on_aws chips
  // can resolve their execution_id by matching on the command string.
  // The persisted Anthropic tool_use blocks don't carry execution_id,
  // so without this lookup the Approve/Reject panel would never
  // re-appear after the live SSE stream completes.
  const refreshExecutions = useCallback(async () => {
    if (!props.sessionId) return;
    try {
      const rows = await listExecutions(props.sessionId);
      setExecutions(rows);
    } catch {
      // Non-fatal — the chip just falls back to "no panel".
    }
  }, [props.sessionId]);

  // Load history + executions when the session id changes.
  useEffect(() => {
    if (!props.sessionId) return;
    let cancelled = false;
    setHistory([]);
    setExecutions([]);
    setStreaming(null);
    setError(null);
    (async () => {
      try {
        const [rows, execs] = await Promise.all([
          getChatHistory(props.sessionId!),
          listExecutions(props.sessionId!),
        ]);
        if (!cancelled) {
          setHistory(rows);
          setExecutions(execs);
        }
      } catch (e) {
        if (!cancelled) setError(toFriendlyError(e));
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [props.sessionId]);

  // Auto-scroll on new content. JSDOM doesn't implement scrollTo, so guard.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [history, streaming]);

  // With no argument, sends (and clears) the typed draft; select-to-ask
  // passes its composed message instead, leaving the draft untouched.
  const send = useCallback(async (overrideMessage?: string) => {
    const message = (overrideMessage ?? draft).trim();
    if (!props.sessionId || !message || busy) return;
    if (overrideMessage === undefined) setDraft("");
    setBusy(true);
    setError(null);

    // Optimistically render the user's own message.
    const userRow: ChatHistoryRow = {
      role: "user",
      content: [{ type: "text", text: message }],
      usage: null,
      sequence: history.length,
    };
    setHistory((h) => [...h, userRow]);
    setStreaming({ text: "", tools: [] });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      for await (const ev of streamChat(props.sessionId, message, controller.signal)) {
        if (ev.event === "delta") {
          setStreaming((s) =>
            s ? { ...s, text: s.text + ev.data.text } : { text: ev.data.text, tools: [] },
          );
        } else if (ev.event === "tool") {
          if (ev.data.type === "tool_use_start") {
            setStreaming((s) =>
              s
                ? { ...s, tools: [...s.tools, { id: ev.data.id, name: ev.data.name }] }
                : { text: "", tools: [{ id: ev.data.id, name: ev.data.name }] },
            );
          } else if (ev.data.type === "tool_use_end") {
            const d = ev.data;
            setStreaming((s) =>
              s
                ? {
                    ...s,
                    tools: s.tools.map((t) =>
                      t.id === d.id
                        ? {
                            ...t,
                            input: d.input,
                            resultPreview: d.result_preview,
                            artifactPath: d.artifact_path,
                            executionId: (d as { execution_id?: number | null }).execution_id ?? null,
                            isError: d.is_error,
                          }
                        : t,
                    ),
                  }
                : s,
            );
            // Each tool_use_end starts a new assistant turn — preserve the
            // current one in history and reset for the next.
            // Actually no: the agent loop persists turns server-side; we
            // just keep accumulating the live state until `done`.
          }
        } else if (ev.event === "error") {
          setError({ kind: "generic", message: ev.data.message });
        } else if (ev.event === "done") {
          // Re-fetch history to get the canonical sequence of messages
          // (the backend persists tool_result user-turns we don't render
          // separately client-side). Also re-fetch executions so the
          // Approve/Reject panel persists into the historical view —
          // without this re-fetch, the chip would lose its executionId
          // on the live → history swap and the panel would vanish.
          const [rows] = await Promise.all([
            getChatHistory(props.sessionId),
            refreshExecutions(),
          ]);
          setHistory(rows);
          setStreaming(null);
        }
      }
    } catch (e) {
      setError(toFriendlyError(e));
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }, [busy, draft, history.length, props.sessionId]);

  // Select-to-ask (issue #56): a click on the viewer's "Ask Claude" overlay
  // arrives as an askRequest with a fresh nonce. Consume the nonce BEFORE
  // the busy/session guards so a click during an in-flight stream is
  // dropped (same rule as the disabled Send button), not queued for later.
  // Unlike the Send button the overlay isn't visibly disabled, so a drop
  // must say so — silence reads as "queued" and the reviewer waits forever.
  const lastAskNonceRef = useRef(0);
  useEffect(() => {
    const req = props.askRequest;
    if (!req || req.nonce === lastAskNonceRef.current) return;
    lastAskNonceRef.current = req.nonce;
    if (!props.sessionId || busy) {
      setError({
        kind: "generic",
        message: busy
          ? "Claude is still replying — click Ask Claude again once this reply finishes."
          : "The session is still loading — try Ask Claude again in a moment.",
      });
      return;
    }
    void send(buildAskMessage(req));
  }, [props.askRequest, props.sessionId, busy, send]);

  const onClear = useCallback(async () => {
    if (!props.sessionId || busy) return;
    if (!confirm("Clear this conversation? The deposit and audit summary stay.")) return;
    await clearChat(props.sessionId);
    setHistory([]);
    setStreaming(null);
  }, [busy, props.sessionId]);

  const onKey = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void send();
      }
    },
    [send],
  );

  if (!props.sessionId) {
    return (
      <div className="flex h-full items-center justify-center bg-ink-100 text-sm text-ink-500">
        Open a paper to start a chat.
      </div>
    );
  }

  // tool_use ids whose result was an error (issue #43 pre-flight rejections,
  // unknown-profile, etc.) — these run_on_aws calls created no Execution row,
  // so they must not shift the positional block→row pairing after a reload.
  const erroredToolUseIds = collectErroredToolUseIds(history);

  return (
    <div className="flex h-full flex-col border-t border-ink-200 bg-ink-100">
      <div className="flex items-center justify-end border-b border-ink-200 px-3 py-1 text-xs uppercase tracking-wide text-ink-500">
        <button
          onClick={onClear}
          className="rounded px-2 py-0.5 text-xs text-ink-500 hover:bg-ink-200 hover:text-ink-700"
          disabled={busy}
        >
          Clear
        </button>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-auto px-3 py-3 text-sm">
        {history.length === 0 && streaming === null && !error && (
          <p className="text-ink-500">
            Ask anything about this deposit. Try: <em>"What tests are in this repo?"</em>{" "}
            or <em>"Does the build work on macOS?"</em>
          </p>
        )}
        {history.map((m, i) => {
          // Index each run_on_aws block in history so HistoryMessage can
          // resolve the Nth *dispatched* block to the Nth Execution row
          // (sorted by id ascending — the order they were created). This
          // positional match is more robust than command-string equality,
          // which breaks on subtle whitespace / encoding differences
          // between the agent loop's persisted tool_use input and the
          // Execution row's persisted command field. run_on_aws calls
          // whose result was an error (e.g. issue #43 pre-flight rejection)
          // created no row, so they're excluded from the count.
          const runOnAwsOffset = countPriorRunOnAws(history, i, erroredToolUseIds);
          return (
            <HistoryMessage
              key={i}
              row={m}
              sessionId={props.sessionId!}
              executions={executions}
              runOnAwsOffset={runOnAwsOffset}
              erroredToolUseIds={erroredToolUseIds}
            />
          );
        })}
        {streaming && (
          <StreamingMessage live={streaming} sessionId={props.sessionId} busy={busy} />
        )}
        {error && (
          <div
            className={`mt-2 rounded border p-2 text-xs ${
              error.kind === "auth"
                ? "border-amber-300 bg-amber-50 text-amber-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
          >
            <div>{error.message}</div>
            {error.kind === "auth" && (
              <a
                href="/api/auth/signin"
                className="mt-1 inline-block font-semibold underline hover:text-amber-700"
              >
                Sign in again →
              </a>
            )}
          </div>
        )}
      </div>

      <form
        className="border-t border-ink-200 bg-white p-2"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={busy ? "Claude is thinking…" : "Ask Claude about this deposit"}
          disabled={busy}
          className="w-full resize-none rounded border border-ink-200 bg-ink-50 px-2 py-1 text-sm focus:border-ink-500 focus:bg-white focus:outline-none disabled:bg-ink-100 disabled:text-ink-500"
        />
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-ink-500">Enter to send · Shift+Enter for newline</span>
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="rounded bg-ink-700 px-3 py-1 text-xs text-white hover:bg-ink-900 disabled:bg-ink-300"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
}

function HistoryMessage(props: {
  row: ChatHistoryRow;
  sessionId: number;
  executions: ExecutionRow[];
  /** Number of dispatched run_on_aws blocks that appear BEFORE this message
   *  in history — used to position-match into the executions list. */
  runOnAwsOffset: number;
  /** tool_use ids whose result was an error (created no Execution row). */
  erroredToolUseIds: Set<string>;
}) {
  const { row } = props;
  if (row.role === "user") {
    // User rows contain either a plain text block (reviewer's message) or
    // tool_result blocks (the agent loop's tool returns). We only render
    // the reviewer-visible kind.
    const textBlocks = row.content.filter((b) => b.type === "text") as Extract<
      AnthropicBlock,
      { type: "text" }
    >[];
    if (textBlocks.length === 0) return null;
    return (
      <div className="mb-2 flex justify-end">
        {/* pre-wrap keeps the line structure of select-to-ask context
            blocks (path, fenced snippet) readable inside the bubble. */}
        <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-lg bg-ink-700 px-3 py-1.5 text-sm text-white">
          {textBlocks.map((b, i) => (
            <span key={i}>{b.text}</span>
          ))}
        </div>
      </div>
    );
  }
  // Assistant
  return (
    <div className="mb-3">
      {row.content.map((b, i) => {
        if (b.type === "text") {
          return <AssistantMarkdown key={i} text={b.text} />;
        }
        if (b.type === "tool_use") {
          // Historical tool_use blocks don't carry execution_id (the
          // persisted Anthropic shape rejects extra fields). Recover by
          // positional match: count which Nth *dispatched* run_on_aws block
          // this is (skipping error results, which created no row) and pair
          // with the Nth Execution row sorted by id.
          let executionId: number | null = null;
          if (b.name === "run_on_aws" && !props.erroredToolUseIds.has(b.id)) {
            const priorInThisMessage = row.content
              .slice(0, i)
              .filter(
                (c) =>
                  c.type === "tool_use" &&
                  c.name === "run_on_aws" &&
                  !props.erroredToolUseIds.has(c.id),
              ).length;
            const idx = props.runOnAwsOffset + priorInThisMessage;
            const sorted = [...props.executions].sort((x, y) => x.id - y.id);
            executionId = sorted[idx]?.id ?? null;
          }
          return (
            <ToolChip
              key={b.id}
              name={b.name}
              input={b.input}
              resultPreview={undefined /* historical chips don't carry preview */}
              artifactPath={undefined}
              executionId={executionId}
              sessionId={props.sessionId}
              compact
            />
          );
        }
        return null;
      })}
    </div>
  );
}

function StreamingMessage(props: {
  live: LiveAssistant;
  sessionId: number;
  busy: boolean;
}) {
  return (
    <div className="mb-3">
      {props.live.text && <AssistantMarkdown text={props.live.text} streaming />}
      {props.live.tools.map((t) => (
        <ToolChip
          key={t.id}
          name={t.name}
          input={t.input}
          resultPreview={t.resultPreview}
          artifactPath={t.artifactPath}
          executionId={t.executionId}
          isError={t.isError}
          sessionId={props.sessionId}
        />
      ))}
      {props.busy && !props.live.text && props.live.tools.length === 0 && (
        <span className="text-ink-500">…</span>
      )}
    </div>
  );
}

function AssistantMarkdown(props: { text: string; streaming?: boolean }) {
  return (
    <article className="prose prose-sm max-w-none text-ink-900">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{props.text}</ReactMarkdown>
      {props.streaming && (
        <span className="inline-block h-3 w-1.5 animate-pulse bg-ink-500 align-middle" />
      )}
    </article>
  );
}

// Re-export for the Reviewer screen to swap ChatBoxStub → ChatBox.
export { rawFileUrl };
// Exported for unit tests (issue #43 block→row pairing).
export { collectErroredToolUseIds, countPriorRunOnAws };

/**
 * Collect the tool_use ids whose tool_result was an error. Those run_on_aws
 * calls (pre-flight rejections, unknown-profile, …) produced no Execution
 * row, so they're excluded from the block→row positional pairing.
 */
function collectErroredToolUseIds(history: ChatHistoryRow[]): Set<string> {
  const ids = new Set<string>();
  for (const row of history) {
    for (const b of row.content) {
      if (b.type === "tool_result" && b.is_error) ids.add(b.tool_use_id);
    }
  }
  return ids;
}

/**
 * Count the *dispatched* run_on_aws tool_use blocks (those whose result was
 * not an error) that appear strictly before the message at `messageIndex`.
 * Used to position-match the Nth historical block to the Nth Execution row.
 */
function countPriorRunOnAws(
  history: ChatHistoryRow[],
  messageIndex: number,
  erroredToolUseIds: Set<string>,
): number {
  let n = 0;
  for (let i = 0; i < messageIndex; i++) {
    for (const b of history[i].content) {
      if (
        b.type === "tool_use" &&
        b.name === "run_on_aws" &&
        !erroredToolUseIds.has(b.id)
      ) {
        n++;
      }
    }
  }
  return n;
}

/**
 * Turn the wide variety of "something went wrong" exceptions streamChat
 * can throw into the {kind, message} shape ChatBox renders. The point is
 * to surface the BACKEND's detail string (e.g. "Your sign-in has
 * expired. Please sign in again.") rather than the SPA-side
 * "API 401" that ApiError.message would give you, AND to mark 401s as
 * `auth` so we can render a sign-in link instead of just a red banner.
 */
function toFriendlyError(
  e: unknown,
): { kind: "auth" | "generic"; message: string } {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: string } | null;
    const detail = body?.detail ?? `API ${e.status}`;
    if (e.status === 401 || e.status === 403) {
      return { kind: "auth", message: detail };
    }
    return { kind: "generic", message: detail };
  }
  if (e instanceof Error) return { kind: "generic", message: e.message };
  return { kind: "generic", message: String(e) };
}
